import {
  AfterViewInit,
  Component,
  computed,
  effect,
  input,
  inject,
  signal,
  OnDestroy,
  output,
  DestroyRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { TimeoutComponent } from "../../../../../../shared/components/timeout/timeout.component";
import { ProgressSpinner } from "@openng/optimus-ui/progressspinner";
import { createTimeoutSignal } from "../../../../../../shared/utils/timeout.utils";
import { NgClass } from "@angular/common";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Chart, ChartDataset, TimeScale, TimeScaleOptions, TimeUnit } from "chart.js";
import "chartjs-adapter-luxon";
import { Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { RealtimeDataPoint } from "../../facade/humidity-statistics.service";
import { DateTime, DateTimeUnit } from "luxon";
import { DropdownOption } from "../../../../../../models/ui/dropdownOption";
import { DatePickerModule, DatePickerTypeView } from "@openng/optimus-ui/datepicker";
import { ChartChangeTimeEvent } from "../../../../../../models/ui/chartData";
import { FormsModule } from "@angular/forms";
import { FloatLabelModule } from "@openng/optimus-ui/floatlabel";
import { TIMEFRAME_OPTIONS } from "../../../../../../constants/timeFrameOptions";
import { DataPoint, LineChartData } from "../../../../../../models/ui/energyToolCharts";
import { BotevoDesignTokens } from "../../../../../../../assets/themes/botevoPreset";

@Component({
  standalone: true,
  selector: "app-humidity-chart",
  imports: [
    TimeoutComponent,
    ProgressSpinner,
    NgClass,
    TranslateModule,
    DatePickerModule,
    FormsModule,
    FloatLabelModule,
  ],
  templateUrl: "./humidity-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./humidity-chart.component.scss",
})
export class HumidityChart implements AfterViewInit, OnDestroy {
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  public chartId = input<string>();
  public chartData = input<LineChartData[] | null>();
  public realtimePoint$ = input<Observable<RealtimeDataPoint>>();

  public changeTimeframeEvent = output<ChartChangeTimeEvent>();

  public loading = computed(() => this.chartData() === null);
  public timeOut = createTimeoutSignal(this.loading);
  public timeFrameOptions: DropdownOption[] = TIMEFRAME_OPTIONS;
  public currentTimeFrame = signal<DateTimeUnit>("day");
  public calendarView = signal<DatePickerTypeView>("date");
  public date = signal<Date>(new Date());
  public dateFormat = signal("dd.mm.yy");

  private lineChart?: Chart<"line", DataPoint[]>;
  private chartInitialized = signal(false);

  constructor() {
    effect(() => {
      const datasets = this.chartData();
      const isReady = this.chartInitialized();

      if (!isReady || !datasets || !this.lineChart) return;

      const lineDataSets: ChartDataset<"line", DataPoint[]>[] = [];
      datasets.forEach((dataset) => {
        if (dataset.data) {
          lineDataSets.push({ ...dataset, data: dataset.data });
        }
      });

      this.lineChart.data.datasets = lineDataSets;
      this.setLabels(this.translate.currentLang);
      this.setXAxisOptions();
      this.lineChart.update();
    });

    effect(() => {
      const stream = this.realtimePoint$();
      const isReady = this.chartInitialized();

      if (!stream || !isReady) return;
    });

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.setLabels(this.translate.currentLang),
    });
  }

  public ngOnDestroy() {
    this.lineChart?.destroy();
  }

  public ngAfterViewInit(): void {
    this.initChart();
    this.chartInitialized.set(true);
    this.realtimePoint$()
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.addDataPoint(data.datasetIndex, data.point, data.replace);
        },
      });
  }

  /**
   * Adds a new data point to the chart data
   * @param datasetIndex The index at which a new point is to be set
   * @param point The data point that is to be inserted
   */
  private addDataPoint(datasetIndex: number, point: { x: string; y: number }, replace?: boolean) {
    if (!this.lineChart) return;

    const dataset = this.lineChart.data.datasets[datasetIndex];
    if (!dataset.data) return;

    if (replace && dataset.data.length > 0) {
      dataset.data[dataset.data.length - 1] = point;
    } else {
      dataset.data.push(point);

      dataset.data.sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());
    }

    this.lineChart.update("none");
  }

  /**
   * Creates the chart configuration
   */
  private initChart() {
    Chart.defaults.font.size = 17;
    const chartId = this.chartId();
    if (!chartId) return;
    this.lineChart = new Chart(chartId, {
      type: "line",
      data: {
        datasets: [],
      },
      options: {
        maintainAspectRatio: false,
        animation: false,
        elements: {
          point: {
            radius: 0.5,
          },
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              color: BotevoDesignTokens.primitive?.gray?.[0],
            },
          },
        },
        scales: {
          y1: {
            position: "left",
            title: {
              display: true,
              color: BotevoDesignTokens.primitive?.grayTransparent?.[400],
            },
            border: {
              display: false,
            },
            grid: {
              color: BotevoDesignTokens.primitive?.grayTransparent?.[400],
            },
            ticks: {
              color: BotevoDesignTokens.primitive?.gray?.[0],
            },
          },
          y2: {
            position: "right",
            title: {
              display: true,
              color: BotevoDesignTokens.primitive?.greenTransparent?.[400],
            },
            border: {
              display: false,
            },
            grid: {
              color: BotevoDesignTokens.primitive?.greenTransparent?.[400],
            },
            ticks: {
              color: BotevoDesignTokens.primitive?.green?.[400],
            },
          },
          x: {
            type: "time",
            adapters: {
              date: {
                locale: "de-DE",
              },
            },
            time: {
              unit: "hour",
              displayFormats: {
                hour: "HH:mm",
              },
            },
            title: {
              display: true,
              text: "",
            },
            grid: {
              display: true,
              drawTicks: true,
              color: BotevoDesignTokens.primitive?.grayTransparent?.[400],
            },
            ticks: {
              display: true,
              maxTicksLimit: 12,
              color: BotevoDesignTokens.primitive?.gray?.[0],
            },
          },
        },
      },
    });
  }

  /**
   * Translates and updates the labels and titles of the line chart based on the given locale.
   * @param lang The locale to use for the translation.
   */
  private setLabels(lang: string) {
    if (!this.lineChart?.data.datasets.length) return;
    this.translate
      .get([
        "humidity_chart.humidity_label",
        "humidity_chart.dewpoint_label",
        "humidity_chart.temperature_label",
        "humidity_chart.temperature_axis",
        "humidity_chart.humidity_axis",
      ])
      .subscribe((translations) => {
        if (this.lineChart!.data.datasets.length >= 2) {
          this.lineChart!.data.datasets[0].label = translations["humidity_chart.humidity_label"];
          this.lineChart!.data.datasets[1].label = translations["humidity_chart.temperature_label"];
          this.lineChart!.data.datasets[2].label = translations["humidity_chart.dewpoint_label"];
          const scales = this.lineChart!.options.scales!;
          if (scales["y1"]?.title) {
            scales["y1"].title.text = translations["humidity_chart.humidity_axis"];
          }
          if (scales["y2"]?.title) {
            scales["y2"].title.text = translations["humidity_chart.temperature_axis"];
          }
          const xScale = scales["x"] as TimeScale["options"];
          if (xScale.adapters.date) {
            (xScale.adapters.date as { locale: string }).locale = lang;
          }
          this.lineChart!.update();
        }
      });
  }

  /**
   * Updates the unit of a time axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param unit The unit to use for the axis.
   */
  private updateTimeseriesUnit(axis: "x" | "y", unit: TimeUnit) {
    const scale = this.lineChart?.options?.scales?.[axis];
    if (scale?.type === "time") {
      const ts = scale as TimeScaleOptions;
      ts.time.unit = unit;
    }
  }

  /**
   * Updates the tooltip format of a time axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param format The format to use for the tooltip.
   */
  private updateTimeseriesTooltipFormat(axis: "x" | "y", format: string) {
    const scale = this.lineChart?.options?.scales?.[axis];
    if (scale?.type === "time") {
      const ts = scale as TimeScaleOptions;
      ts.time.tooltipFormat = format;
    }
  }

  /**
   * Updates the display format of a time axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param format The format to use for the display.
   */
  private updateTimeseriesDisplayFormat(axis: "x" | "y", format: string) {
    const scale = this.lineChart?.options?.scales?.[axis];
    if (scale?.type === "time") {
      const ts = scale as TimeScaleOptions;
      ts.time.displayFormats["day"] = format;
    }
  }

  /**
   * Configures the x-axis of the bar chart based on the current filter aggregation.
   * - For "day": Sets time unit to "hour" and tooltip format to "HH:mm".
   * - For "week": Sets time unit to "day", display format to "EEE dd.MM.", and tooltip format to "dd.MM.yyyy".
   * - For "month": Sets time unit to "day", display format to "d", and tooltip format to "dd. MMM".
   * - For "year": Sets time unit to "month" and tooltip format to "MMM yyyy".
   */
  private setXAxisOptions() {
    const start = DateTime.fromMillis(this.date().getTime()).startOf(this.currentTimeFrame()).valueOf();

    const scale = this.lineChart?.options?.scales?.["x"];
    if (scale) {
      scale.min = start;
    }
    switch (this.currentTimeFrame()) {
      case "day":
        this.updateTimeseriesUnit("x", "hour");
        this.updateTimeseriesTooltipFormat("x", "HH:mm");
        break;
      case "week":
        this.updateTimeseriesUnit("x", "day");
        this.updateTimeseriesDisplayFormat("x", "EEE dd.MM.");
        this.updateTimeseriesTooltipFormat("x", "dd.MM.yyyy");
        break;
      case "month":
        this.updateTimeseriesUnit("x", "day");
        this.updateTimeseriesDisplayFormat("x", "d");
        this.updateTimeseriesTooltipFormat("x", "dd. MMM");
        break;
      case "year":
        this.updateTimeseriesUnit("x", "month");
        this.updateTimeseriesTooltipFormat("x", "MMM yyyy");
        break;
    }
  }

  /**
   * Handles the selection of an aggregation option to update the chart view.
   * Adjusts the calendar view and date format based on the selected option,
   * and updates the time window accordingly.
   *
   * @param option - The selected aggregation option ("day", "week", "month", or "year").
   */

  public clickTimeframeOption(option: string | number | boolean) {
    this.currentTimeFrame.set(option as DateTimeUnit);
    switch (option) {
      case "day":
        this.calendarView.set("date");
        this.dateFormat.set("dd.mm.yy");
        break;
      case "week":
        // To display KW (week of year) set date Format to the formated value (via luxon) and on dateChange, prev and next window . so everytime date changes
        this.calendarView.set("date");
        this.dateFormat.set("dd.mm.yy");
        break;
      case "month":
        this.calendarView.set("month");
        this.dateFormat.set("mm.yy");
        break;
      case "year":
        this.calendarView.set("year");
        this.dateFormat.set("yy");
        break;
    }
    this.date.set(new Date(this.date()));
    this.setTimeWindow();
  }

  /**
   * Move the time window backward by one unit of the current filter aggregation.
   * Then calls setTimeWindow() to update the time window.
   */
  public previousTimeWindow() {
    this.date.set(
      DateTime.fromMillis(this.date().getTime())
        .minus({
          [this.currentTimeFrame()]: 1,
        })
        .toJSDate()
    );

    this.setTimeWindow();
  }

  /**
   * Move the time window forward by one unit of the current filter aggregation.
   * Then calls setTimeWindow() to update the time window.
   */
  public nextTimeWindow() {
    this.date.set(
      DateTime.fromMillis(this.date().getTime())
        .plus({
          [this.currentTimeFrame()]: 1,
        })
        .toJSDate()
    );

    this.setTimeWindow();
  }

  /**
   * Sets the time window by calculating the start and end timestamps based on the current date
   * and the current filter aggregation. Emits a changeTimeframeEvent with the calculated start,
   * end, and timeframe.
   */
  public setTimeWindow() {
    const start = DateTime.fromMillis(this.date().getTime())
      .startOf(this.currentTimeFrame() as DateTimeUnit)
      .valueOf();
    const end = DateTime.fromMillis(this.date().getTime())
      .endOf(this.currentTimeFrame() as DateTimeUnit)
      .valueOf();
    this.changeTimeframeEvent.emit({
      start: start,
      end: end,
      timeFrame: this.currentTimeFrame(),
    });
  }
}
